import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, apiPost, apiPut, apiDelete, apiPatch, getUploadUrl } from '../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Building2, ArrowLeft, Plus, Trash2, Users, Settings,
  Map, Save, X, MapPin, Search, FileSpreadsheet, Shield, ShieldOff,
  RefreshCw, Wifi, BarChart3, TrendingUp, CheckCircle, DoorOpen, Clock,
  ToggleLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

// ==================== FLOOR MANAGER ====================
function FloorManager() {
  const [floors, setFloors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newFloorName, setNewFloorName] = useState('');
  const [newFloorOrder, setNewFloorOrder] = useState(0);
  const [newFloorImage, setNewFloorImage] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadFloors = useCallback(async () => {
    try {
      const data = await apiGet('/floors');
      setFloors(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadFloors(); }, [loadFloors]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newFloorImage || !newFloorName) return;
    setCreating(true);
    try {
      const formData = new FormData();
      formData.append('name', newFloorName);
      formData.append('sort_order', newFloorOrder);
      formData.append('map_image', newFloorImage);
      await apiPost('/floors', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Floor created');
      setNewFloorName('');
      setNewFloorOrder(0);
      setNewFloorImage(null);
      setShowCreate(false);
      loadFloors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create floor');
    } finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    try {
      await apiDelete(`/floors/${id}`);
      toast.success('Floor deleted');
      loadFloors();
    } catch (err) { toast.error('Failed to delete floor'); }
    setDeleteTarget(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Floor Maps</h3>
        <Button onClick={() => setShowCreate(true)} data-testid="admin-floor-upload-button">
          <Plus className="h-4 w-4 mr-2" /> Add Floor
        </Button>
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Floor</DialogTitle>
            <DialogDescription>Upload a floor map image and configure the floor.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>Floor Name</Label>
              <Input
                placeholder="e.g. Level 3 - East Wing"
                value={newFloorName}
                onChange={(e) => setNewFloorName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={newFloorOrder}
                onChange={(e) => setNewFloorOrder(parseInt(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <Label>Map Image</Label>
              <Input
                type="file"
                accept=".png,.jpg,.jpeg,.webp"
                onChange={(e) => setNewFloorImage(e.target.files[0])}
                required
              />
              <p className="text-xs text-muted-foreground">
                Recommended: 1200-1600px wide. Larger images will be auto-scaled to fit the viewer.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={creating}>
                {creating ? 'Uploading...' : 'Create Floor'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      ) : floors.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <Map className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No floors configured yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {floors.map(floor => (
            <Card key={floor.id}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {floor.map_image_key && (
                    <img
                      src={getUploadUrl(floor.map_image_key)}
                      alt={floor.name}
                      className="w-32 h-20 object-cover rounded-lg border"
                    />
                  )}
                  <div className="flex-1">
                    <h4 className="font-semibold">{floor.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {floor.map_width} x {floor.map_height}px • Order: {floor.sort_order}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="secondary">{floor.total_desks || 0} desks</Badge>
                      <Badge variant="secondary">{floor.hot_desk_count || 0} hot</Badge>
                      <Badge variant="outline">{floor.fixed_desk_count || 0} fixed</Badge>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(floor.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Floor</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this floor and all its desks. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteTarget)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ==================== DESK EDITOR ====================
function DeskEditor() {
  const [floors, setFloors] = useState([]);
  const [selectedFloorId, setSelectedFloorId] = useState('');
  const [desks, setDesks] = useState([]);
  const [floorData, setFloorData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [editingDesk, setEditingDesk] = useState(null);
  const [deskForm, setDeskForm] = useState({ label: '', type: 'hot_desk', assigned_user_id: '', tags: '' });
  const [users, setUsers] = useState([]);
  const [draggingDesk, setDraggingDesk] = useState(null);
  const [deleteDeskTarget, setDeleteDeskTarget] = useState(null);
  const imageRef = useRef(null);

  useEffect(() => {
    apiGet('/floors').then(setFloors).catch(console.error);
    apiGet('/users').then(setUsers).catch(() => setUsers([]));
  }, []);

  useEffect(() => {
    if (!selectedFloorId) return;
    setLoading(true);
    const today = format(new Date(), 'yyyy-MM-dd');
    apiGet(`/floors/${selectedFloorId}/desks`, { date: today })
      .then(data => {
        setDesks(data.desks || []);
        setFloorData(data.floor || null);
        setLoading(false);
      })
      .catch(err => { console.error(err); setLoading(false); });
  }, [selectedFloorId]);

  const handleMapClick = async (e) => {
    if (!placing || !imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const label = prompt('Enter desk label (e.g. 3E-05):');
    if (!label) return;

    try {
      await apiPost(`/floors/${selectedFloorId}/desks`, {
        label, x_percent: Math.round(x * 100) / 100, y_percent: Math.round(y * 100) / 100, type: 'hot_desk'
      });
      toast.success(`Desk ${label} placed`);
      const today = format(new Date(), 'yyyy-MM-dd');
      const data = await apiGet(`/floors/${selectedFloorId}/desks`, { date: today });
      setDesks(data.desks || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to place desk');
    }
  };

  const handleDeskDragEnd = async (desk, e) => {
    if (!imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    if (x < 0 || x > 100 || y < 0 || y > 100) return;

    try {
      await apiPut(`/desks/${desk.id}`, {
        x_percent: Math.round(x * 100) / 100,
        y_percent: Math.round(y * 100) / 100
      });
      toast.success(`Desk ${desk.label} repositioned`);
      const today = format(new Date(), 'yyyy-MM-dd');
      const data = await apiGet(`/floors/${selectedFloorId}/desks`, { date: today });
      setDesks(data.desks || []);
    } catch (err) {
      toast.error('Failed to move desk');
    }
    setDraggingDesk(null);
  };

  const handleEditDesk = (desk) => {
    setEditingDesk(desk);
    setDeskForm({
      label: desk.label,
      type: desk.type,
      assigned_user_id: desk.assigned_user_id || '',
      tags: Array.isArray(desk.tags) ? desk.tags.join(', ') : (desk.tags || '')
    });
  };

  const handleSaveDesk = async () => {
    if (!editingDesk) return;
    try {
      const tagsStr = deskForm.tags ? JSON.stringify(deskForm.tags.split(',').map(t => t.trim()).filter(Boolean)) : null;
      await apiPut(`/desks/${editingDesk.id}`, {
        label: deskForm.label,
        type: deskForm.type,
        assigned_user_id: deskForm.type === 'fixed' ? (deskForm.assigned_user_id || null) : null,
        tags: tagsStr
      });
      toast.success('Desk updated');
      setEditingDesk(null);
      const today = format(new Date(), 'yyyy-MM-dd');
      const data = await apiGet(`/floors/${selectedFloorId}/desks`, { date: today });
      setDesks(data.desks || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update desk');
    }
  };

  const handleDeleteDesk = async (deskId) => {
    try {
      await apiDelete(`/desks/${deskId}`);
      toast.success('Desk deleted');
      const today = format(new Date(), 'yyyy-MM-dd');
      const data = await apiGet(`/floors/${selectedFloorId}/desks`, { date: today });
      setDesks(data.desks || []);
    } catch (err) { toast.error('Failed to delete desk'); }
    setDeleteDeskTarget(null);
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const result = await apiPost(`/floors/${selectedFloorId}/desks/bulk`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Imported ${result.created} desks${result.errors.length > 0 ? ` (${result.errors.length} errors)` : ''}`);
      const today = format(new Date(), 'yyyy-MM-dd');
      const data = await apiGet(`/floors/${selectedFloorId}/desks`, { date: today });
      setDesks(data.desks || []);
    } catch (err) {
      toast.error('CSV import failed');
    }
    e.target.value = '';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={selectedFloorId} onValueChange={setSelectedFloorId}>
          <SelectTrigger className="w-[250px]">
            <SelectValue placeholder="Select a floor" />
          </SelectTrigger>
          <SelectContent>
            {floors.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>

        {selectedFloorId && (
          <>
            <Button
              variant={placing ? 'default' : 'outline'}
              onClick={() => setPlacing(!placing)}
              data-testid="admin-place-desk-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              {placing ? 'Click Map to Place' : 'Place Mode'}
            </Button>

            <label>
              <Button variant="outline" asChild>
                <span>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  CSV Import
                  <input type="file" accept=".csv" className="hidden" onChange={handleCsvImport} />
                </span>
              </Button>
            </label>
          </>
        )}
      </div>

      {selectedFloorId && floorData && (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4" data-testid="admin-desk-editor-canvas">
          {/* Map Canvas */}
          <Card className="overflow-hidden">
            <div className="relative bg-muted/30">
              {placing && (
                <div className="absolute top-2 left-2 z-10">
                  <Badge variant="default" className="animate-pulse">Click on map to place desk</Badge>
                </div>
              )}
              <img
                ref={imageRef}
                src={getUploadUrl(floorData.map_image_key)}
                alt={floorData.name}
                className={`block w-full ${placing ? 'cursor-crosshair' : ''}`}
                onClick={handleMapClick}
                draggable={false}
              />
              {/* Desk markers */}
              {desks.map(desk => (
                <button
                  key={desk.id}
                  className={`absolute transform -translate-x-1/2 -translate-y-1/2
                    rounded-full border-2 shadow-sm cursor-move z-10
                    h-6 w-6 flex items-center justify-center text-[8px] font-bold text-white
                    ${desk.type === 'fixed' ? 'bg-[hsl(210,90%,46%)] border-[hsl(210,80%,36%)]' : 'bg-[hsl(142,70%,38%)] border-[hsl(142,60%,30%)]'}
                    ${editingDesk?.id === desk.id ? 'ring-2 ring-yellow-400 scale-125' : ''}
                    hover:scale-110 transition-transform duration-100
                  `}
                  style={{ left: `${desk.x_percent}%`, top: `${desk.y_percent}%` }}
                  onClick={(e) => { e.stopPropagation(); handleEditDesk(desk); }}
                  draggable
                  onDragEnd={(e) => handleDeskDragEnd(desk, e)}
                  data-testid={`admin-desk-marker-${desk.label}`}
                >
                  {desk.label.slice(-2)}
                </button>
              ))}
            </div>
          </Card>

          {/* Properties Panel */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Desk Properties</CardTitle>
              <CardDescription className="text-xs">
                {editingDesk ? `Editing: ${editingDesk.label}` : 'Click a desk marker to edit'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4" data-testid="admin-desk-properties-form">
              {editingDesk ? (
                <>
                  <div className="space-y-2">
                    <Label className="text-xs">Label</Label>
                    <Input
                      value={deskForm.label}
                      onChange={(e) => setDeskForm(f => ({ ...f, label: e.target.value }))}
                      placeholder="e.g. 3E-04"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Type</Label>
                    <Select value={deskForm.type} onValueChange={(v) => setDeskForm(f => ({ ...f, type: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hot_desk">Hot Desk</SelectItem>
                        <SelectItem value="fixed">Fixed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {deskForm.type === 'fixed' && (
                    <div className="space-y-2">
                      <Label className="text-xs">Assigned User</Label>
                      <Select
                        value={deskForm.assigned_user_id}
                        onValueChange={(v) => setDeskForm(f => ({ ...f, assigned_user_id: v }))}
                      >
                        <SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                        <SelectContent>
                          {users.map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.display_name} ({u.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-xs">Tags (comma separated)</Label>
                    <Input
                      value={deskForm.tags}
                      onChange={(e) => setDeskForm(f => ({ ...f, tags: e.target.value }))}
                      placeholder="standing desk, dual monitor"
                    />
                  </div>
                  <Separator />
                  <div className="flex gap-2">
                    <Button className="flex-1" onClick={handleSaveDesk} data-testid="admin-desk-save-button">
                      <Save className="h-4 w-4 mr-2" /> Save
                    </Button>
                    <Button variant="destructive" size="icon" onClick={() => setDeleteDeskTarget(editingDesk.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Position: {editingDesk.x_percent?.toFixed(1)}%, {editingDesk.y_percent?.toFixed(1)}%
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  Select a desk marker on the map to edit its properties, or use Place Mode to add new desks.
                </p>
              )}

              <Separator />
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">All Desks ({desks.length})</p>
                <ScrollArea className="h-[200px]">
                  {desks.map(d => (
                    <div
                      key={d.id}
                      className={`flex items-center justify-between py-1.5 px-2 rounded text-xs hover:bg-muted/50 cursor-pointer ${editingDesk?.id === d.id ? 'bg-muted' : ''}`}
                      onClick={() => handleEditDesk(d)}
                    >
                      <span className="font-medium">{d.label}</span>
                      <Badge variant="outline" className="text-[10px]">{d.type}</Badge>
                    </div>
                  ))}
                </ScrollArea>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {!selectedFloorId && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <MapPin className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>Select a floor to manage desks</p>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={!!deleteDeskTarget} onOpenChange={(open) => !open && setDeleteDeskTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Desk</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this desk and any associated bookings. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteDesk(deleteDeskTarget)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ==================== USER MANAGER ====================
function UserManager() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadUsers = useCallback(async () => {
    try {
      const data = await apiGet('/users');
      setUsers(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleToggleRole = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    try {
      await apiPatch(`/users/${userId}/role`, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      loadUsers();
    } catch (err) {
      toast.error('Failed to update role');
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">User Management</h3>
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      ) : (
        <div className="space-y-2">
          {users.map(u => (
            <Card key={u.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      {u.photo_url && <AvatarImage src={u.photo_url} alt={u.display_name} />}
                      <AvatarFallback className="text-xs bg-primary/10 text-primary font-semibold">
                        {u.display_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{u.display_name}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                      {u.role === 'admin' ? <Shield className="h-3 w-3 mr-1" /> : null}
                      {u.role}
                    </Badge>
                    {u.id !== currentUser?.id && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleRole(u.id, u.role)}
                            >
                              {u.role === 'admin' ? <ShieldOff className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {u.role === 'admin' ? 'Remove admin' : 'Make admin'}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== BOOKING RULES ====================
function BookingRulesManager() {
  const [rules, setRules] = useState(null);
  const [blackouts, setBlackouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxDays, setMaxDays] = useState(14);
  const [maxConcurrent, setMaxConcurrent] = useState(3);
  const [newBlackout, setNewBlackout] = useState('');
  const [newBlackoutReason, setNewBlackoutReason] = useState('');

  const loadRules = useCallback(async () => {
    try {
      const data = await apiGet('/booking-rules');
      setRules(data.rules);
      setBlackouts(data.blackout_dates || []);
      setMaxDays(data.rules?.max_days_in_advance || 14);
      setMaxConcurrent(data.rules?.max_concurrent_per_user || 3);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadRules(); }, [loadRules]);

  const handleSaveRules = async () => {
    try {
      await apiPut('/booking-rules', {
        max_days_in_advance: maxDays,
        max_concurrent_per_user: maxConcurrent
      });
      toast.success('Booking rules updated');
      loadRules();
    } catch (err) { toast.error('Failed to update rules'); }
  };

  const handleAddBlackout = async () => {
    if (!newBlackout) return;
    try {
      await apiPost('/booking-rules/blackout-dates', { date: newBlackout, reason: newBlackoutReason });
      toast.success('Blackout date added');
      setNewBlackout('');
      setNewBlackoutReason('');
      loadRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add blackout date');
    }
  };

  const handleRemoveBlackout = async (date) => {
    try {
      await apiDelete(`/booking-rules/blackout-dates/${date}`);
      toast.success('Blackout date removed');
      loadRules();
    } catch (err) { toast.error('Failed to remove blackout date'); }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Booking Rules</h3>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">General Rules</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">Max Days in Advance</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={maxDays}
                onChange={(e) => setMaxDays(parseInt(e.target.value) || 14)}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Max Concurrent Bookings</Label>
              <Input
                type="number"
                min={1}
                max={50}
                value={maxConcurrent}
                onChange={(e) => setMaxConcurrent(parseInt(e.target.value) || 3)}
              />
            </div>
          </div>
          <Button onClick={handleSaveRules}>
            <Save className="h-4 w-4 mr-2" /> Save Rules
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Blackout Dates</CardTitle>
          <CardDescription className="text-xs">Days when booking is not allowed</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              type="date"
              value={newBlackout}
              onChange={(e) => setNewBlackout(e.target.value)}
              className="w-[180px]"
            />
            <Input
              placeholder="Reason (optional)"
              value={newBlackoutReason}
              onChange={(e) => setNewBlackoutReason(e.target.value)}
              className="flex-1"
            />
            <Button variant="outline" onClick={handleAddBlackout}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {blackouts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No blackout dates configured</p>
          ) : (
            <div className="space-y-1">
              {blackouts.map(b => (
                <div key={b.date} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50">
                  <div>
                    <span className="text-sm font-medium">{b.date}</span>
                    {b.reason && <span className="text-xs text-muted-foreground ml-2">— {b.reason}</span>}
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleRemoveBlackout(b.date)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== ANALYTICS DASHBOARD ====================
function AnalyticsDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet('/admin/stats')
      .then(data => { setStats(data); setLoading(false); })
      .catch(err => { console.error(err); setLoading(false); });
  }, []);

  if (loading) return (
    <div className="flex justify-center py-12">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
    </div>
  );

  if (!stats) return <p className="text-muted-foreground text-center py-8">Failed to load stats</p>;

  const statCards = [
    { label: 'Total Users', value: stats.total_users, icon: Users, color: 'text-blue-600 bg-blue-500/10' },
    { label: 'Hot Desks', value: stats.hot_desks, icon: MapPin, color: 'text-emerald-600 bg-emerald-500/10' },
    { label: 'Fixed Desks', value: stats.fixed_desks, icon: Building2, color: 'text-indigo-600 bg-indigo-500/10' },
    { label: 'Today\'s Bookings', value: stats.today.bookings, icon: BarChart3, color: 'text-orange-600 bg-orange-500/10' },
    { label: 'Check-ins Today', value: stats.today.checkins, icon: CheckCircle, color: 'text-teal-600 bg-teal-500/10' },
    { label: 'Utilization', value: `${stats.today.utilization_percent}%`, icon: TrendingUp, color: 'text-purple-600 bg-purple-500/10' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-analytics-dashboard">
      <h3 className="text-lg font-semibold">Dashboard</h3>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {statCards.map((s, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${s.color}`}>
                  <s.icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{s.value}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Work Location Breakdown */}
      {Object.keys(stats.location_breakdown).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Work Location Breakdown (Today)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 flex-wrap">
              {Object.entries(stats.location_breakdown).map(([loc, count]) => (
                <div key={loc} className="flex items-center gap-2">
                  <span className={`h-3 w-3 rounded-full ${
                    loc === 'office' ? 'bg-emerald-500' :
                    loc === 'remote' ? 'bg-blue-500' :
                    loc === 'elsewhere' ? 'bg-amber-500' : 'bg-gray-400'
                  }`} />
                  <span className="text-sm capitalize">{loc}: <strong>{count}</strong></span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Popular Desks */}
      {stats.popular_desks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Most Popular Desks (Last 7 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.popular_desks.map((desk, i) => (
                <div key={i} className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium w-6 text-muted-foreground">#{i + 1}</span>
                    <span className="text-sm font-medium">{desk.label}</span>
                    <span className="text-xs text-muted-foreground">({desk.floor})</span>
                  </div>
                  <Badge variant="secondary">{desk.bookings} bookings</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Weekly Bookings */}
      {stats.week.daily_bookings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Bookings This Week</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-32">
              {stats.week.daily_bookings.map((day, i) => {
                const maxCount = Math.max(...stats.week.daily_bookings.map(d => d.count), 1);
                const height = (day.count / maxCount) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs font-medium">{day.count}</span>
                    <div
                      className="w-full bg-primary/20 rounded-t transition-all duration-300"
                      style={{ height: `${Math.max(height, 4)}%` }}
                    />
                    <span className="text-[10px] text-muted-foreground">
                      {day.date.slice(5)}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ==================== WORK LOCATION SYNC ====================
function WorkLocationSync() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncDate, setSyncDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const loadLocations = useCallback(async () => {
    try {
      const data = await apiGet('/work-locations', { date: syncDate });
      setLocations(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [syncDate]);

  useEffect(() => { loadLocations(); }, [loadLocations]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await apiPost(`/work-locations/sync?date=${syncDate}`);
      toast.success(`Synced ${result.synced} users for ${result.date}`);
      loadLocations();
    } catch (err) {
      toast.error('Sync failed');
    } finally { setSyncing(false); }
  };

  const locationColors = {
    office: 'bg-emerald-500/10 text-emerald-700 border-emerald-200',
    remote: 'bg-blue-500/10 text-blue-700 border-blue-200',
    elsewhere: 'bg-amber-500/10 text-amber-700 border-amber-200',
    unknown: 'bg-gray-500/10 text-gray-500 border-gray-200',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Work Location Sync</h3>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={syncDate}
            onChange={(e) => setSyncDate(e.target.value)}
            className="w-[180px]"
            data-testid="sync-date-input"
          />
          <Button onClick={handleSync} disabled={syncing} data-testid="admin-sync-locations-button">
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Simulate MS Graph Sync'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Current Locations for {syncDate}</CardTitle>
          <CardDescription className="text-xs">
            Shows where each user is working. Use "Simulate MS Graph Sync" to randomize locations (simulates what Microsoft Graph would provide).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : locations.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No location data for this date. Run a sync to populate.
            </p>
          ) : (
            <div className="space-y-2">
              {locations.map(loc => (
                <div key={loc.user_id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/30">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      {loc.photo_url && <AvatarImage src={loc.photo_url} alt={loc.display_name} />}
                      <AvatarFallback className="text-xs bg-primary/10 text-primary font-semibold">
                        {loc.display_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{loc.display_name}</p>
                      <p className="text-xs text-muted-foreground">{loc.email}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={locationColors[loc.location] || locationColors.unknown}>
                    {loc.location}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== MEETING ROOM EDITOR ====================
function MeetingRoomEditor() {
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedFloorId, setSelectedFloorId] = useState('');
  const [floorData, setFloorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(null); // room being placed
  const [editingRoom, setEditingRoom] = useState(null);
  const imageRef = useRef(null);

  useEffect(() => {
    Promise.all([
      apiGet('/floors'),
      apiGet('/graph/rooms')
    ]).then(([floorData, roomData]) => {
      setFloors(floorData);
      setRooms(roomData);
      setLoading(false);
    }).catch(err => { console.error(err); setLoading(false); });
  }, []);

  // Load floor map when floor is selected
  useEffect(() => {
    if (!selectedFloorId) { setFloorData(null); return; }
    const today = format(new Date(), 'yyyy-MM-dd');
    apiGet(`/floors/${selectedFloorId}/desks`, { date: today })
      .then(data => setFloorData(data.floor || null))
      .catch(console.error);
  }, [selectedFloorId]);

  const handleMapClick = async (e) => {
    if (!placing || !imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    try {
      await apiPut(`/graph/rooms/${placing.id}`, {
        floor_id: selectedFloorId,
        x_percent: Math.round(x * 100) / 100,
        y_percent: Math.round(y * 100) / 100
      });
      toast.success(`${placing.display_name} placed on map`);
      // Refresh rooms
      const updated = await apiGet('/graph/rooms');
      setRooms(updated);
      setPlacing(null);
    } catch (err) {
      toast.error('Failed to place room');
    }
  };

  const handleDragEnd = async (room, e) => {
    if (!imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    if (x < 0 || x > 100 || y < 0 || y > 100) return;

    try {
      await apiPut(`/graph/rooms/${room.id}`, {
        floor_id: selectedFloorId,
        x_percent: Math.round(x * 100) / 100,
        y_percent: Math.round(y * 100) / 100
      });
      toast.success(`${room.display_name} repositioned`);
      const updated = await apiGet('/graph/rooms');
      setRooms(updated);
    } catch (err) {
      toast.error('Failed to move room');
    }
  };

  const handleRemoveFromMap = async (room) => {
    try {
      await apiPut(`/graph/rooms/${room.id}`, {
        floor_id: null,
        x_percent: null,
        y_percent: null
      });
      toast.success(`${room.display_name} removed from map`);
      const updated = await apiGet('/graph/rooms');
      setRooms(updated);
      setEditingRoom(null);
    } catch (err) {
      toast.error('Failed to remove room');
    }
  };

  const placedOnFloor = rooms.filter(r => r.floor_id === selectedFloorId && r.x_percent != null);
  const unplaced = rooms.filter(r => !r.floor_id || r.x_percent == null);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="meeting-room-editor">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Meeting Rooms</h3>
        <Badge variant="secondary">{rooms.length} synced rooms</Badge>
      </div>

      {rooms.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <DoorOpen className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No meeting rooms synced yet</p>
            <p className="text-xs mt-1">Go to M365 Integration tab and click "Sync Meeting Rooms" to pull rooms from Microsoft 365.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Floor selector and placement controls */}
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={selectedFloorId} onValueChange={setSelectedFloorId}>
              <SelectTrigger className="w-[250px]" data-testid="room-floor-select">
                <SelectValue placeholder="Select a floor to place rooms" />
              </SelectTrigger>
              <SelectContent>
                {floors.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>

            {placing && (
              <Badge variant="default" className="animate-pulse" data-testid="room-placing-badge">
                Click map to place: {placing.display_name}
                <button className="ml-2" onClick={() => setPlacing(null)}><X className="h-3 w-3" /></button>
              </Badge>
            )}
          </div>

          {/* Map + Room list layout */}
          {selectedFloorId && floorData && (
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4">
              {/* Map Canvas */}
              <Card className="overflow-hidden">
                <div className="relative bg-muted/30">
                  {placing && (
                    <div className="absolute top-2 left-2 z-10">
                      <Badge variant="default" className="animate-pulse">Click to place: {placing.display_name}</Badge>
                    </div>
                  )}
                  <img
                    ref={imageRef}
                    src={getUploadUrl(floorData.map_image_key)}
                    alt={floorData.name}
                    className={`block w-full ${placing ? 'cursor-crosshair' : ''}`}
                    onClick={handleMapClick}
                    draggable={false}
                  />
                  {/* Room markers on this floor */}
                  {placedOnFloor.map(room => (
                    <button
                      key={room.id}
                      className={`absolute transform -translate-x-1/2 -translate-y-1/2
                        rounded-md border-2 shadow-sm cursor-move z-10
                        h-7 w-7 flex items-center justify-center text-[7px] font-bold
                        bg-amber-100 border-amber-400 text-amber-700
                        ${editingRoom?.id === room.id ? 'ring-2 ring-yellow-400 scale-125' : ''}
                        hover:scale-110 transition-transform duration-100`}
                      style={{ left: `${room.x_percent}%`, top: `${room.y_percent}%` }}
                      onClick={(e) => { e.stopPropagation(); setEditingRoom(room); }}
                      draggable
                      onDragEnd={(e) => handleDragEnd(room, e)}
                      data-testid={`room-marker-${room.display_name}`}
                    >
                      RM
                    </button>
                  ))}
                </div>
              </Card>

              {/* Room Properties / List Panel */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Room Details</CardTitle>
                  <CardDescription className="text-xs">
                    {editingRoom ? editingRoom.display_name : 'Click a room marker to view details'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {editingRoom ? (
                    <>
                      <div className="space-y-2">
                        <Label className="text-xs">Name</Label>
                        <p className="text-sm font-medium">{editingRoom.display_name}</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Email</Label>
                        <p className="text-sm text-muted-foreground">{editingRoom.email}</p>
                      </div>
                      {editingRoom.capacity > 0 && (
                        <div className="space-y-2">
                          <Label className="text-xs">Capacity</Label>
                          <p className="text-sm">{editingRoom.capacity} people</p>
                        </div>
                      )}
                      {editingRoom.building && (
                        <div className="space-y-2">
                          <Label className="text-xs">Building</Label>
                          <p className="text-sm">{editingRoom.building}</p>
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground">
                        Position: {editingRoom.x_percent?.toFixed(1)}%, {editingRoom.y_percent?.toFixed(1)}%
                      </div>
                      <Separator />
                      <Button variant="destructive" size="sm" className="w-full" onClick={() => handleRemoveFromMap(editingRoom)} data-testid="room-remove-from-map">
                        <X className="h-4 w-4 mr-2" /> Remove from Map
                      </Button>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                      Select a room marker on the map, or pick an unplaced room below.
                    </p>
                  )}

                  <Separator />

                  {/* Placed rooms on this floor */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                      On this floor ({placedOnFloor.length})
                    </p>
                    <ScrollArea className="h-[120px]">
                      {placedOnFloor.map(r => (
                        <div
                          key={r.id}
                          className={`flex items-center justify-between py-1.5 px-2 rounded text-xs hover:bg-muted/50 cursor-pointer ${editingRoom?.id === r.id ? 'bg-muted' : ''}`}
                          onClick={() => setEditingRoom(r)}
                        >
                          <span className="font-medium truncate">{r.display_name}</span>
                          <Badge variant="outline" className="text-[10px] shrink-0">placed</Badge>
                        </div>
                      ))}
                      {placedOnFloor.length === 0 && (
                        <p className="text-xs text-muted-foreground text-center py-2">No rooms placed on this floor</p>
                      )}
                    </ScrollArea>
                  </div>

                  {/* Unplaced rooms */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                      Unplaced rooms ({unplaced.length})
                    </p>
                    <ScrollArea className="h-[160px]">
                      {unplaced.map(r => (
                        <div
                          key={r.id}
                          className="flex items-center justify-between py-1.5 px-2 rounded text-xs hover:bg-muted/50"
                        >
                          <div className="min-w-0">
                            <span className="font-medium truncate block">{r.display_name}</span>
                            {r.building && <span className="text-muted-foreground">{r.building}</span>}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 text-[10px] shrink-0"
                            onClick={() => setPlacing(r)}
                            data-testid={`place-room-${r.display_name}`}
                          >
                            <MapPin className="h-3 w-3 mr-1" /> Place
                          </Button>
                        </div>
                      ))}
                      {unplaced.length === 0 && (
                        <p className="text-xs text-muted-foreground text-center py-2">All rooms placed</p>
                      )}
                    </ScrollArea>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {!selectedFloorId && (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <MapPin className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Select a floor to place meeting rooms</p>
                <p className="text-xs mt-1">Meeting rooms synced from M365 need to be manually positioned on each floor map.</p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ==================== COMBINED FLOOR MANAGER (Floors + Desks + Rooms) ====================
function FloorManagerCombined() {
  const [subTab, setSubTab] = useState('maps');

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 border-b">
        <button
          data-testid="floor-subtab-maps"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            subTab === 'maps'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setSubTab('maps')}
        >
          <Map className="h-4 w-4 inline mr-1.5 -mt-0.5" />
          Floor Maps
        </button>
        <button
          data-testid="floor-subtab-desks"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            subTab === 'desks'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setSubTab('desks')}
        >
          <MapPin className="h-4 w-4 inline mr-1.5 -mt-0.5" />
          Desks
        </button>
        <button
          data-testid="floor-subtab-rooms"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            subTab === 'rooms'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setSubTab('rooms')}
        >
          <DoorOpen className="h-4 w-4 inline mr-1.5 -mt-0.5" />
          Rooms
        </button>
      </div>

      {subTab === 'maps' && <FloorManager />}
      {subTab === 'desks' && <DeskEditor />}
      {subTab === 'rooms' && <MeetingRoomEditor />}
    </div>
  );
}

// ==================== M365 INTEGRATION ====================
function M365Integration() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncingUsers, setSyncingUsers] = useState(false);
  const [syncingPresence, setSyncingPresence] = useState(false);
  const [syncingRooms, setSyncingRooms] = useState(false);
  const [assigningDesks, setAssigningDesks] = useState(false);
  const [syncingWorkLocation, setSyncingWorkLocation] = useState(false);

  // Local form state
  const [deskAttribute, setDeskAttribute] = useState('officeLocation');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterCompany, setFilterCompany] = useState('');

  const [userSyncInterval, setUserSyncInterval] = useState('24');
  const [roomSyncInterval, setRoomSyncInterval] = useState('24');
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);

  const loadSettings = useCallback(async () => {
    try {
      const data = await apiGet('/admin/settings');
      setSettings(data);
      if (data.m365_desk_attribute) setDeskAttribute(data.m365_desk_attribute);
      if (data.user_sync_interval) setUserSyncInterval(data.user_sync_interval);
      if (data.room_sync_interval) setRoomSyncInterval(data.room_sync_interval);
      if (data.auto_sync_enabled !== undefined) setAutoSyncEnabled(data.auto_sync_enabled === 'true');
      if (data.m365_user_filter) {
        try {
          const f = JSON.parse(data.m365_user_filter);
          setFilterDepartment(f.department || '');
          setFilterCompany(f.companyName || '');
        } catch (err) { console.error('Failed to parse m365_user_filter:', err); }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const filterObj = {};
      if (filterDepartment) filterObj.department = filterDepartment;
      if (filterCompany) filterObj.companyName = filterCompany;

      await apiPut('/admin/settings', {
        m365_desk_attribute: deskAttribute,
        m365_user_filter: JSON.stringify(filterObj),
        user_sync_interval: userSyncInterval,
        room_sync_interval: roomSyncInterval,
        auto_sync_enabled: autoSyncEnabled ? 'true' : 'false',
      });
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally { setSaving(false); }
  };

  const handleSyncUsers = async () => {
    setSyncingUsers(true);
    try {
      const result = await apiPost('/graph/users');
      const removedMsg = result.removed > 0 ? `, ${result.removed} removed` : '';
      toast.success(`Synced ${result.total} users (${result.created} new, ${result.updated} updated${removedMsg})`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'User sync failed — check Azure AD credentials');
    } finally { setSyncingUsers(false); }
  };

  const handleSyncPresence = async () => {
    setSyncingPresence(true);
    try {
      const result = await apiPost('/graph/presence');
      toast.success(`Live presence fetched for ${result.total_users} users`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Presence fetch failed');
    } finally { setSyncingPresence(false); }
  };

  const handleSyncRooms = async () => {
    setSyncingRooms(true);
    try {
      const result = await apiPost('/graph/rooms');
      toast.success(`Synced ${result.synced} meeting rooms`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Room sync failed');
    } finally { setSyncingRooms(false); }
  };

  const handleAssignDesks = async () => {
    setAssigningDesks(true);
    try {
      const result = await apiPost('/graph/assign-desks');
      toast.success(`Assigned ${result.assigned} desks (${result.not_found} labels not found)`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Desk assignment failed');
    } finally { setAssigningDesks(false); }
  };

  const handleSyncWorkLocations = async () => {
    setSyncingWorkLocation(true);
    try {
      const result = await apiPost('/graph/work-locations');
      toast.success(`Live work locations fetched for ${result.total_users} users`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Work location check failed');
    } finally { setSyncingWorkLocation(false); }
  };

  const commonAttributes = [
    { value: 'officeLocation', label: 'Office Location (officeLocation)' },
    { value: 'physicalDeliveryOfficeName', label: 'Physical Office Name (physicalDeliveryOfficeName)' },
    { value: 'streetAddress', label: 'Street Address' },
    { value: 'city', label: 'City' },
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Desk Attribute Mapping */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Desk Label Mapping</CardTitle>
          <CardDescription>
            Choose which M365 user attribute contains the desk label. Users with a matching value will be auto-assigned to the corresponding fixed desk.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>M365 User Attribute</Label>
            <Select value={deskAttribute} onValueChange={setDeskAttribute}>
              <SelectTrigger data-testid="m365-desk-attribute-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {commonAttributes.map(attr => (
                  <SelectItem key={attr.value} value={attr.value}>{attr.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-2">
              <Label className="text-xs text-muted-foreground">Or enter a custom attribute name:</Label>
              <Input
                placeholder="e.g. extension_abc123_DeskNumber"
                value={deskAttribute}
                onChange={(e) => setDeskAttribute(e.target.value)}
                className="mt-1"
                data-testid="m365-desk-attribute-custom"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">User Sync Filter</CardTitle>
          <CardDescription>
            Filter which M365 users are synced to DeskFlow. Leave blank to sync all users.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Department</Label>
              <Input
                placeholder="e.g. Engineering"
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                data-testid="m365-filter-department"
              />
            </div>
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                placeholder="e.g. Contoso"
                value={filterCompany}
                onChange={(e) => setFilterCompany(e.target.value)}
                data-testid="m365-filter-company"
              />
            </div>
          </div>
          <div className="p-3 rounded-lg bg-muted/50 space-y-2">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-emerald-600 shrink-0" />
              <Label className="text-sm font-medium">User filtering</Label>
            </div>
            <p className="text-xs text-muted-foreground pl-6">
              Only active member accounts are synced. Disabled accounts, guest accounts, and shared mailboxes are automatically excluded. Previously synced users that no longer pass the filter are removed along with their bookings and desk assignments.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Sync Schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sync Schedule</CardTitle>
          <CardDescription>
            Configure how often data is automatically synced from Microsoft 365. Changes take effect after saving settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Switch
              checked={autoSyncEnabled}
              onCheckedChange={setAutoSyncEnabled}
              data-testid="auto-sync-enabled"
            />
            <Label className="text-sm">Enable automatic syncing</Label>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">Presence</Label>
              <div className="flex items-center gap-2 h-10 px-3 rounded-md border bg-muted/50">
                <Wifi className="h-3.5 w-3.5 text-emerald-500" />
                <span className="text-xs text-muted-foreground">Live from Graph</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">User Sync</Label>
              <Select value={userSyncInterval} onValueChange={setUserSyncInterval} data-testid="user-sync-interval-select">
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">Every 6 hours</SelectItem>
                  <SelectItem value="12">Every 12 hours</SelectItem>
                  <SelectItem value="24">Once a day</SelectItem>
                  <SelectItem value="0">Manual only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Room Sync</Label>
              <Select value={roomSyncInterval} onValueChange={setRoomSyncInterval} data-testid="room-sync-interval-select">
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">Every 6 hours</SelectItem>
                  <SelectItem value="12">Every 12 hours</SelectItem>
                  <SelectItem value="24">Once a day</SelectItem>
                  <SelectItem value="0">Manual only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
            <Clock className="h-4 w-4 shrink-0" />
            <span>
              Presence polling runs in the browser when users view the map.
              User and room syncs run server-side via Cloudflare Cron Triggers (configure in wrangler.toml).
            </span>
          </div>

          <Button onClick={handleSaveSettings} disabled={saving} data-testid="m365-save-settings">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save All Settings'}
          </Button>
        </CardContent>
      </Card>

      {/* Sync Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sync Actions</CardTitle>
          <CardDescription>
            Sync users and rooms from Microsoft 365. Presence and work locations are now fetched live from Graph on each request.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <Button onClick={handleSyncUsers} disabled={syncingUsers} variant="outline" className="h-auto py-3 flex-col gap-1" data-testid="m365-sync-users">
              <Users className={`h-5 w-5 ${syncingUsers ? 'animate-spin' : ''}`} />
              <span className="text-xs">{syncingUsers ? 'Syncing...' : 'Sync Users'}</span>
            </Button>
            <Button onClick={handleSyncPresence} disabled={syncingPresence} variant="outline" className="h-auto py-3 flex-col gap-1" data-testid="m365-sync-presence">
              <Wifi className={`h-5 w-5 ${syncingPresence ? 'animate-spin' : ''}`} />
              <span className="text-xs">{syncingPresence ? 'Checking...' : 'Check Presence'}</span>
            </Button>
            <Button onClick={handleSyncWorkLocations} disabled={syncingWorkLocation} variant="outline" className="h-auto py-3 flex-col gap-1" data-testid="m365-sync-work-locations">
              <Building2 className={`h-5 w-5 ${syncingWorkLocation ? 'animate-spin' : ''}`} />
              <span className="text-xs">{syncingWorkLocation ? 'Checking...' : 'Check Work Locations'}</span>
            </Button>
            <Button onClick={handleSyncRooms} disabled={syncingRooms} variant="outline" className="h-auto py-3 flex-col gap-1" data-testid="m365-sync-rooms">
              <Map className={`h-5 w-5 ${syncingRooms ? 'animate-spin' : ''}`} />
              <span className="text-xs">{syncingRooms ? 'Syncing...' : 'Sync Meeting Rooms'}</span>
            </Button>
            <Button onClick={handleAssignDesks} disabled={assigningDesks} variant="outline" className="h-auto py-3 flex-col gap-1" data-testid="m365-assign-desks">
              <MapPin className={`h-5 w-5 ${assigningDesks ? 'animate-spin' : ''}`} />
              <span className="text-xs">{assigningDesks ? 'Assigning...' : 'Auto-Assign Desks'}</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== FEATURE SETTINGS ====================
function FeatureSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowFixedBooking, setAllowFixedBooking] = useState(false);
  const [requireCheckin, setRequireCheckin] = useState(false);
  const [checkinWindow, setCheckinWindow] = useState('30');

  useEffect(() => {
    apiGet('/admin/settings')
      .then(data => {
        setAllowFixedBooking(data.allow_fixed_desk_booking_when_away === 'true');
        setRequireCheckin(data.require_checkin === 'true');
        if (data.checkin_window_minutes) setCheckinWindow(data.checkin_window_minutes);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiPut('/admin/settings', {
        allow_fixed_desk_booking_when_away: allowFixedBooking ? 'true' : 'false',
        require_checkin: requireCheckin ? 'true' : 'false',
        checkin_window_minutes: checkinWindow,
      });
      toast.success('Feature settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex justify-center py-8">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="feature-settings">
      <h3 className="text-lg font-semibold">Feature Settings</h3>

      {/* Allow booking fixed desks when away */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Book Fixed Desks When Away
          </CardTitle>
          <CardDescription>
            When enabled, users can book a permanently assigned desk if the assigned person is working remotely or out of office for that day.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <Label htmlFor="allow-fixed-booking" className="text-sm">
              Allow temporary booking of fixed desks
            </Label>
            <Switch
              id="allow-fixed-booking"
              checked={allowFixedBooking}
              onCheckedChange={setAllowFixedBooking}
              data-testid="toggle-allow-fixed-booking"
            />
          </div>
          {allowFixedBooking && (
            <div className="mt-3 p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
              Fixed desks will appear as green (bookable) on the map when the assigned user's work location is set to remote, elsewhere, or out of office.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Require check-in */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Require Check-in
          </CardTitle>
          <CardDescription>
            When enabled, users must check in to confirm their booking. Unchecked bookings can be identified for follow-up.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="require-checkin" className="text-sm">
              Require check-in for bookings
            </Label>
            <Switch
              id="require-checkin"
              checked={requireCheckin}
              onCheckedChange={setRequireCheckin}
              data-testid="toggle-require-checkin"
            />
          </div>
          {requireCheckin && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-xs">Check-in Window (minutes)</Label>
                <Select value={checkinWindow} onValueChange={setCheckinWindow} data-testid="checkin-window-select">
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="60">60 minutes</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                Users will be reminded to check in when they view their booking. Bookings without check-in will be flagged in the dashboard.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} data-testid="save-feature-settings">
        <Save className="h-4 w-4 mr-2" />
        {saving ? 'Saving...' : 'Save Feature Settings'}
      </Button>
    </div>
  );
}

// ==================== MAIN ADMIN PAGE ====================
export default function AdminPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto p-3 md:p-6">
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold">Admin Panel</h1>
            <p className="text-xs md:text-sm text-muted-foreground">Manage floors, desks, users, and booking rules</p>
          </div>
        </div>

        <Tabs defaultValue="dashboard" data-testid="admin-tabs">
          <div className="overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
            <TabsList className="mb-4 md:mb-6 inline-flex w-max md:w-auto md:flex-wrap">
              <TabsTrigger value="dashboard" className="text-xs md:text-sm">
                <BarChart3 className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1 md:mr-2" /> Dashboard
              </TabsTrigger>
              <TabsTrigger value="floors" className="text-xs md:text-sm">
                <Map className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1 md:mr-2" /> Floors
              </TabsTrigger>
              <TabsTrigger value="users" className="text-xs md:text-sm">
                <Users className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1 md:mr-2" /> Users
              </TabsTrigger>
              <TabsTrigger value="rules" className="text-xs md:text-sm">
                <Settings className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1 md:mr-2" /> Rules
              </TabsTrigger>
              <TabsTrigger value="features" className="text-xs md:text-sm">
                <ToggleLeft className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1 md:mr-2" /> Features
              </TabsTrigger>
              <TabsTrigger value="m365" className="text-xs md:text-sm">
                <RefreshCw className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1 md:mr-2" /> M365
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="dashboard"><AnalyticsDashboard /></TabsContent>
          <TabsContent value="floors"><FloorManagerCombined /></TabsContent>
          <TabsContent value="users"><UserManager /></TabsContent>
          <TabsContent value="rules"><BookingRulesManager /></TabsContent>
          <TabsContent value="features"><FeatureSettings /></TabsContent>
          <TabsContent value="m365"><M365Integration /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
